"use client";

import React, { useState } from "react";
import Link from "next/link";
import {
  TrendingUp,
  BarChart3,
  ArrowRight,
  Target,
  Scale,
} from "lucide-react";
import { WhaleIcon } from "@/components/icons/whale-icon";
import { cn } from "@/lib/utils";
import { LANDING_STATS } from "@/lib/landing-constants";
import { useReveal } from "@/lib/use-reveal";
import { trackMarketingCtaClick } from "@/utils/analytics";

const products = [
  {
    id: "ev-optimizers",
    title: "Optimizer",
    description:
      "Review fair odds, edge size, and cross-book context in one ranked workflow.",
    image: "/images/landing/homepage-proof/optimizer-game-lines-2026-09-29.webp",
    mobileImage: "/images/landing/homepage-proof/optimizer-game-lines-2026-09-29-mobile.webp",
    href: "/tools/sports-betting-optimizer",
    proof: "Silver+",
    icon: TrendingUp,
    color: "primary",
    features: [
      "Fair odds and edge size on every play",
      "Props, spreads, totals, and moneylines",
      "Side-by-side odds across every book",
      "Filter by edge %, sport, book, or market",
    ],
  },
  {
    id: "odds-screen",
    title: "Odds Screen",
    description:
      `Compare live odds across ${LANDING_STATS.SPORTSBOOKS_SCANNED_DISPLAY} books with the best available price highlighted.`,
    image: "/images/landing/ev-gamelines.webp",
    mobileImage: "/images/landing/ev-gamelines-mobile.webp",
    href: "/tools/odds-screen",
    proof: "Silver, Gold, Diamond",
    icon: BarChart3,
    color: "primary",
    features: [
      `Live odds across ${LANDING_STATS.SPORTSBOOKS_SCANNED_DISPLAY} books and DFS apps`,
      "Best available price highlighted on every market",
      "Moneyline, spread, and total in one view",
      "Refreshes in under three seconds",
    ],
  },
  {
    id: "arbitrage",
    title: "Arbitrage + Low Hold",
    description:
      "See the exact stake split for arbitrage and find tighter markets for hedges and promo conversion.",
    image: "/images/landing/homepage-proof/arbitrage-2026-09-29.webp",
    mobileImage: "/images/landing/homepage-proof/arbitrage-2026-09-29-mobile.webp",
    href: "/tools/arbitrage",
    proof: "Silver+",
    icon: Scale,
    color: "success",
    features: [
      "Exact stake split for each arbitrage",
      "Best sportsbook pairings per opportunity",
      "Low-hold markets for hedges and promos",
      "Win regardless of the outcome",
    ],
  },
  {
    id: "esports",
    title: "Prop Discrepancies",
    description:
      "Compare player-prop lines across supported Books, sports, and esports leagues.",
    image: "/images/landing/esports.webp",
    mobileImage: "/images/landing/esports-mobile.webp",
    href: "/tools/esports-discrepancies",
    proof: "Silver+",
    icon: BarChart3,
    color: "warning",
    features: [
      "Traditional sports and esports",
      "Player-prop prices across platforms",
      "Market average vs. individual book",
      "Filter by Sport, League, Books, and edge size",
    ],
  },
  {
    id: "whale-watch",
    title: "Whale Watch",
    description:
      "See where big bets land, which books are slow to react, and where the better odds are still available.",
    image: "/images/landing/homepage-proof/whale-watch-2026-09-29.webp",
    mobileImage: "/images/landing/homepage-proof/whale-watch-2026-09-29-mobile.webp",
    href: "/tools/whale-watch",
    proof: "Gold+",
    icon: WhaleIcon,
    color: "primary",
    features: [
      "See big bets as they happen",
      "Spot when a book is slow to move its odds",
      "Find where the better odds are still live",
      "Track moves across major sportsbooks",
    ],
  },
  {
    id: "ev-sniper",
    title: "+EV Sniper",
    description:
      "Every sport, market, and book on one live board. Expand any row for book-by-book odds and the reason behind the edge.",
    image: "/images/landing/ev-sniper.webp",
    mobileImage: "/images/landing/ev-sniper-mobile.webp",
    href: "#pricing",
    proof: "Diamond",
    icon: Target,
    color: "primary",
    features: [
      "Every sport, market, and book on one board",
      "Ranked by edge size in real time",
      "Expand any row for book-by-book odds",
      "See the reason behind each edge",
    ],
  },
];

const colorClasses = {
  primary: "bg-primary/10 text-primary border-primary/30",
  success: "bg-success/10 text-success border-success/30",
  warning: "bg-warning/10 text-warning border-warning/30",
};

function MarketingToolLink({
  href,
  ctaId,
  className,
  children,
}: {
  href: string;
  ctaId: string;
  className: string;
  children: React.ReactNode;
}) {
  const onClick = () =>
    trackMarketingCtaClick({
      surface: "product_showcase",
      cta: ctaId,
      destination: href,
    });
  if (href.startsWith("#")) {
    return (
      <a href={href} onClick={onClick} className={className}>
        {children}
      </a>
    );
  }
  return (
    <Link href={href} onClick={onClick} className={className}>
      {children}
    </Link>
  );
}

function ProductTab({
  product,
  isActive,
  panelId,
  onClick,
}: {
  product: (typeof products)[0];
  isActive: boolean;
  panelId: string;
  onClick: () => void;
}) {
  const Icon = product.icon;

  return (
    <button
      type="button"
      role="tab"
      id={`product-tab-${product.id}`}
      aria-selected={isActive}
      aria-controls={panelId}
      tabIndex={isActive ? 0 : -1}
      onClick={onClick}
      className={cn(
        "relative flex items-center gap-3 p-4 rounded-xl border press-scale text-left w-full",
        isActive
          ? `${colorClasses[product.color as keyof typeof colorClasses]} shadow-lg`
          : "border-border bg-card shadow-sm hover:shadow-md",
      )}
    >
      <div
        className={cn(
          "p-2 rounded-lg transition-colors",
          isActive ? colorClasses[product.color as keyof typeof colorClasses] : "bg-muted/80 border border-border/50"
        )}
      >
        <Icon className="size-5" />
      </div>
      <div className="flex-1 min-w-0">
        <h3 className="font-semibold text-foreground">{product.title}</h3>
      </div>
      {isActive && (
        <div className="absolute right-4 size-2 rounded-full bg-current animate-reveal-in" />
      )}
    </button>
  );
}

function MobileProductPills({
  activeIndex,
  panelId,
  onSelect,
}: {
  activeIndex: number;
  panelId: string;
  onSelect: (i: number) => void;
}) {
  const pillClasses = {
    primary: "bg-primary/15 text-primary border-primary/40",
    success: "bg-success/15 text-success border-success/40",
    warning: "bg-warning/15 text-warning border-warning/40",
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    let next = activeIndex;
    if (e.key === "ArrowRight" || e.key === "ArrowDown") {
      next = (activeIndex + 1) % products.length;
    } else if (e.key === "ArrowLeft" || e.key === "ArrowUp") {
      next = (activeIndex - 1 + products.length) % products.length;
    } else if (e.key === "Home") {
      next = 0;
    } else if (e.key === "End") {
      next = products.length - 1;
    } else {
      return;
    }
    e.preventDefault();
    onSelect(next);
    const tabId = `product-tab-mobile-${products[next].id}`;
    document.getElementById(tabId)?.focus();
  };

  return (
    <div
      role="tablist"
      aria-label="Products"
      tabIndex={-1}
      onKeyDown={handleKeyDown}
      className="flex gap-2 overflow-x-auto pb-2 -mx-4 px-4 scrollbar-none lg:hidden"
    >
      {products.map((p, i) => {
        const Icon = p.icon;
        const isActive = i === activeIndex;
        return (
          <button
            key={p.id}
            type="button"
            role="tab"
            id={`product-tab-mobile-${p.id}`}
            aria-selected={isActive}
            aria-controls={panelId}
            tabIndex={isActive ? 0 : -1}
            onClick={() => onSelect(i)}
            className={cn(
              "flex items-center gap-2 px-4 py-2.5 rounded-full border whitespace-nowrap press-scale shrink-0",
              isActive
                ? `${pillClasses[p.color as keyof typeof pillClasses]} shadow-md`
                : "border-border bg-card/60 text-muted-foreground"
            )}
          >
            <Icon className="size-4" />
            <span className="text-sm font-medium">{p.title}</span>
          </button>
        );
      })}
    </div>
  );
}

export function ProductShowcase() {
  const [sectionRef, sectionInView] = useReveal<HTMLElement>();
  const [activeProduct, setActiveProduct] = useState(0);
  const product = products[activeProduct];
  const Icon = product.icon;

  const panelId = "product-showcase-panel";

  const handleImageError = (event: React.SyntheticEvent<HTMLImageElement>) => {
    const img = event.currentTarget;
    if (img.dataset.fallbackApplied === "true") return;
    img.dataset.fallbackApplied = "true";
    img.src = "/images/sportsbooks/upside_logo.png";
  };

  const colors = {
    primary: { badge: "bg-primary/10 text-primary border-primary/30", stat: "text-primary" },
    success: { badge: "bg-success/10 text-success border-success/30", stat: "text-success" },
    warning: { badge: "bg-warning/10 text-warning border-warning/30", stat: "text-warning" },
  }[product.color as "primary" | "success" | "warning"];

  return (
    <section ref={sectionRef} className="py-20 bg-background">
      <div className="max-w-7xl mx-auto px-4">
        <div data-revealed={sectionInView} className="reveal text-center mb-10 lg:mb-16">
          <h2 className="text-2xl md:text-4xl lg:text-5xl font-semibold tracking-[-0.035em] text-balance mb-3 lg:mb-4">
            Six tools.{" "}
            <span className="text-primary">One live market.</span>
          </h2>
        </div>

        <MobileProductPills
          activeIndex={activeProduct}
          panelId={panelId}
          onSelect={setActiveProduct}
        />

        <div className="grid lg:grid-cols-[350px_1fr] gap-6 lg:gap-8 mt-4 lg:mt-0">
          <div
            data-revealed={sectionInView}
            className="reveal hidden lg:block space-y-3"
          >
            <div
              role="tablist"
              aria-label="Products"
              tabIndex={-1}
              onKeyDown={(e: React.KeyboardEvent) => {
                let next = activeProduct;
                if (e.key === "ArrowDown" || e.key === "ArrowRight") {
                  next = (activeProduct + 1) % products.length;
                } else if (e.key === "ArrowUp" || e.key === "ArrowLeft") {
                  next = (activeProduct - 1 + products.length) % products.length;
                } else if (e.key === "Home") {
                  next = 0;
                } else if (e.key === "End") {
                  next = products.length - 1;
                } else {
                  return;
                }
                e.preventDefault();
                setActiveProduct(next);
                const tabId = `product-tab-${products[next].id}`;
                document.getElementById(tabId)?.focus();
              }}
              className="space-y-3"
            >
              {products.map((p, index) => (
                <ProductTab
                  key={p.id}
                  product={p}
                  isActive={index === activeProduct}
                  panelId={panelId}
                  onClick={() => setActiveProduct(index)}
                />
              ))}
            </div>

            <div className="pt-4 mt-4 border-t border-border/50">
              <a
                href="#pricing"
                onClick={() =>
                  trackMarketingCtaClick({
                    surface: "product_showcase",
                    cta: "compare_plans",
                    destination: "#pricing",
                  })
                }
                className="inline-flex items-center gap-2 text-primary font-medium hover:underline"
              >
                Compare plans
                <ArrowRight className="size-4" />
              </a>
            </div>
          </div>

          <div
            role="tabpanel"
            id={panelId}
            aria-labelledby={`product-tab-${product.id} product-tab-mobile-${product.id}`}
            key={product.id}
            className="animate-reveal-in"
          >
            <div className="rounded-2xl overflow-hidden border border-border bg-card shadow-sm surface-toplight">
              <div className="relative aspect-[4/3] sm:aspect-[16/9] lg:aspect-[21/9] overflow-hidden">
                <picture>
                  <source media="(max-width: 767px)" srcSet={product.mobileImage} />
                  <img
                    src={product.image}
                    alt={product.title}
                    loading="lazy"
                    className="absolute inset-0 size-full object-cover object-top"
                    onError={handleImageError}
                  />
                </picture>
                <div className="absolute inset-0 bg-gradient-to-t from-card via-transparent to-transparent" />
              </div>

              <div className="p-4 sm:p-6">
                <div className="flex items-start gap-3 sm:gap-4 mb-4">
                  <div className={cn("p-2.5 sm:p-3 rounded-xl border shrink-0", colors.badge)}>
                    <Icon className="size-5 sm:w-6 sm:h-6" />
                  </div>
                  <div className="min-w-0">
                    <h3 className="text-lg sm:text-xl font-bold text-foreground">{product.title}</h3>
                    <p className="text-sm sm:text-base text-muted-foreground mt-1 leading-relaxed">{product.description}</p>
                  </div>
                </div>

                <div className={cn("inline-flex items-center rounded-full border px-3 py-1 text-xs font-medium data-num", colors.badge)}>
                  {product.proof}
                </div>

                <div className="grid sm:grid-cols-2 gap-2.5 sm:gap-3 mt-5">
                  {product.features.map((feature, i) => (
                    <div
                      key={feature}
                      style={{ animationDelay: `${100 + i * 40}ms` }}
                      className="flex items-start gap-2 animate-reveal-in"
                    >
                      <div className={cn("w-1.5 h-1.5 rounded-full mt-1.5 shrink-0", colors.stat.replace("text-", "bg-"))} />
                      <span className="text-sm text-foreground/80">{feature}</span>
                    </div>
                  ))}
                </div>

                <div className="mt-5 pt-4 border-t border-border/40">
                  <MarketingToolLink
                    href={product.href}
                    ctaId={product.id}
                    className="inline-flex items-center gap-1.5 text-primary text-sm font-medium hover:underline"
                  >
                    Explore {product.title}
                    <ArrowRight className="size-3.5" />
                  </MarketingToolLink>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
